import React from 'react';
import {
  booleanOptions2,
  ConditionType,
  Filter,
  FilterOption,
  getCondition,
  getRelationalExpressionProps,
  isNullOperator,
  RelationsProps,
  Select,
  selectorWidth,
  StringConditionType,
} from '@fabric-ui/components';
import { emptyObject } from '@fabric-ui/utils';
import { useGetValuesOptions } from '../../views/Logs/hooks';
import FilterInput from './components/FilterInput';

interface StringNumberTypeBoxProps {
  filter: Filter;
  setFilter: any;
  size: any;
  isVertical: boolean;
  typeCondition: ConditionType;
  options: FilterOption[];
  supportParser?: boolean;
  timeRange?: Date[];
  disabled?: boolean;
  relationsProps: RelationsProps;
  readOnly?: boolean;
  resetOperatorOnChange?: boolean;
  filterInputProps?: Record<string, any>;
}
const FilterTypeBox: React.FC<StringNumberTypeBoxProps> = ({
  filter,
  setFilter,
  size,
  isVertical,
  typeCondition,
  options,
  supportParser,
  timeRange,
  disabled = false,
  readOnly,
  relationsProps = emptyObject as RelationsProps,
  resetOperatorOnChange = false,
  filterInputProps,
}) => {
  const compoundOperator = getCondition(filter, typeCondition, supportParser ? StringConditionType.EQUALS : undefined);
  const [operator] = compoundOperator.split('|');
  const { additionalSelectOptions = [], enableCaseSensitive = false, enableSide = false } = relationsProps;

  const innerExpression = filter.expression?.arrayCondition?.underlying || filter.expression;

  const setValue = value => {
    const wasToken = resetOperatorOnChange;
    const firstOption = options[0]?.value ?? operator;
    const [firstBaseOperator, firstResolution] = firstOption.split('|');
    const effectiveOperator = wasToken ? firstBaseOperator : operator;
    const underlying = {
      fieldName: innerExpression?.fieldName,
      [typeCondition]: { [effectiveOperator]: value },
      ...getRelationalExpressionProps({ operator: effectiveOperator, typeCondition, enableCaseSensitive, enableSide }),
    };
    const existingResolution = filter.expression?.arrayCondition?.resolution;
    const newResolution = wasToken && firstResolution ? firstResolution : existingResolution;
    return setFilter({
      ...filter,
      expression: filter.expression?.arrayCondition ? { arrayCondition: { underlying, resolution: newResolution } } : underlying,
    });
  };

  const value = isNullOperator(operator) ? '' : innerExpression?.[typeCondition]?.[operator];

  const width = isVertical ? selectorWidth : 250;
  const isDisabled = isNullOperator(operator) || !innerExpression?.fieldName || disabled;

  const commonProps = {
    size,
    readOnly,
    disabled: isDisabled,
    value,
    onChange: setValue,
    placeholder: isDisabled ? '' : 'Type Value',
  };

  const enableAutoCompleteValue =
    !!supportParser &&
    [StringConditionType.EQUALS, StringConditionType.NOT_EQUAL].includes(operator as StringConditionType) &&
    !!innerExpression?.fieldName;
  const { data: openSelectOptions, isLoading } = useGetValuesOptions({
    fieldName: innerExpression?.fieldName,
    enabled: enableAutoCompleteValue,
    timeRange,
  });
  return [ConditionType.bool, ConditionType.field /* , ConditionType.enum */].includes(typeCondition) ? (
    <Select
      {...commonProps}
      style={{ width }}
      isRequired
      muiProps={{ disablePortal: true }}
      options={[
        ...(typeCondition === ConditionType.bool ? booleanOptions2 : additionalSelectOptions),
        ...(isNullOperator(operator) ? [{ title: '', value: '' }] : []),
      ]}
    />
  ) : (
    <FilterInput
      {...commonProps}
      inputWidth={width}
      numberField={typeCondition === ConditionType.number}
      operator={compoundOperator}
      options={options}
      optionsUseQueryProps={enableAutoCompleteValue ? { options: openSelectOptions, isLoading } : undefined}
      skeletonLoading
      {...filterInputProps}
    />
  );
};

export default FilterTypeBox;
